import { useCallback, useState } from "react";
import { ExternalLink, Minimize2, PanelRightOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import BntAsistentePanelContent from "@/components/asistente/BntAsistentePanelContent";
import BntAsistenteClearHistoryButton from "@/components/asistente/BntAsistenteClearHistoryButton";
import { useAuth } from "@/lib/auth";
import { useBntAsistente } from "@/modules/asistente/BntAsistenteContext";
import {
  openBntAsistenteWindow,
  shouldPreferIntegratedAssistantPanel,
} from "@/modules/asistente/bntAsistenteRoutes";
import { isBntAsistenteFloatingUiEnabled } from "@/modules/asistente/bntAsistenteFeatureFlags";
import { normalizeMainSystemPath } from "@/modules/asistente/bntAsistenteMessaging";

export default function BntAsistentePanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contentKey, setContentKey] = useState(0);
  const [designerChatTarget, setDesignerChatTarget] = useState(null);
  const {
    panelState,
    activeTab,
    setActiveTab,
    autoOpenEnabled,
    setAutoOpen,
    summary,
    loading,
    reload,
    minimizePanel,
    unreadTabs,
    chatPrivateFocus,
  } = useBntAsistente();

  const preferIntegrated = shouldPreferIntegratedAssistantPanel();
  const floatingUiEnabled = isBntAsistenteFloatingUiEnabled();

  const handleOpenInMainSystem = useCallback((path) => {
    navigate(normalizeMainSystemPath(path));
    minimizePanel();
  }, [navigate, minimizePanel]);

  const handlePersonalNotesChanged = useCallback(() => {
    reload?.();
  }, [reload]);

  const handleAssignedTasksChanged = useCallback(() => {
    reload?.();
  }, [reload]);

  const handleHistoryCleared = useCallback(() => {
    reload?.();
    setContentKey((k) => k + 1);
  }, [reload]);

  if (panelState !== "open") return null;

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-[44] bg-black/20 sm:bg-black/10"
        aria-label="Minimizar asistente"
        onClick={minimizePanel}
        data-testid="bnt-asistente-backdrop"
      />
      <aside
        className="fixed inset-y-0 right-0 z-[46] flex w-full max-w-[440px] flex-col border-l border-slate-200 bg-[#F4F6F8] shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bnt-asistente-panel-title"
        data-testid="bnt-asistente-panel"
      >
        <header className="shrink-0 border-b border-slate-200 bg-white px-3 py-2 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h2 id="bnt-asistente-panel-title" className="text-sm font-bold text-[#0F2D5C] m-0 truncate">
                BNT Asistente
              </h2>
              <p className="text-[10px] text-slate-500 m-0">
                No se puede cerrar: minimice para seguir trabajando (queda en la barra de tareas)
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-sm h-7 px-2"
                onClick={minimizePanel}
                title="Minimizar a barra de tareas"
                data-testid="bnt-asistente-minimize"
              >
                <Minimize2 className="h-3.5 w-3.5" aria-hidden />
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5">
            <BntAsistenteClearHistoryButton
              onCleared={handleHistoryCleared}
              target={designerChatTarget}
              requireTarget
            />
            {!preferIntegrated ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-sm h-7 text-[11px] flex-1 min-w-[120px]"
                onClick={() => openBntAsistenteWindow({ tab: activeTab })}
                data-testid="bnt-asistente-open-popup"
              >
                <ExternalLink className="h-3 w-3 mr-1" aria-hidden />
                Ventana separada
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-sm h-7 text-[11px] flex-1 min-w-[120px]"
              onClick={() => handleOpenInMainSystem("/")}
              data-testid="bnt-asistente-open-full"
            >
              <PanelRightOpen className="h-3 w-3 mr-1" aria-hidden />
              Sistema completo
            </Button>
          </div>

          {floatingUiEnabled ? (
          <div className="flex items-center justify-between gap-2 rounded-sm border border-slate-200 bg-slate-50 px-2 py-1.5">
            <Label htmlFor="bnt-asistente-auto-open" className="text-[10px] text-slate-600 m-0 cursor-pointer">
              Abrir automáticamente con alertas o mensajes
            </Label>
            <Switch
              id="bnt-asistente-auto-open"
              checked={autoOpenEnabled}
              onCheckedChange={setAutoOpen}
              data-testid="bnt-asistente-auto-open"
            />
          </div>
          ) : null}
        </header>

        <div className="flex-1 min-h-0">
          <BntAsistentePanelContent
            key={contentKey}
            user={user}
            summary={summary}
            loading={loading}
            tab={activeTab}
            onTabChange={setActiveTab}
            showHeaderCounts={false}
            onOpenInMainSystem={handleOpenInMainSystem}
            onOpenAlert={handleOpenInMainSystem}
            onPersonalNotesChanged={handlePersonalNotesChanged}
            onAssignedTasksChanged={handleAssignedTasksChanged}
            unreadTabs={unreadTabs}
            chatPrivateFocus={chatPrivateFocus}
            designerChatTarget={designerChatTarget}
            onDesignerChatTargetChange={setDesignerChatTarget}
          />
        </div>
      </aside>
    </>
  );
}
